import { useEffect, useState } from 'react';
import logger from '~/services/logger';
import type { Feed, FeedEntry } from '~/services/layout/index.ts';
import { isEmptyArray } from '~/services/layout/layout';
import {
  filterSkeletonEntries,
  generateSkeletonEntries,
  getLimit,
} from '~/services/layout/layout';
import { getClientFeedUrl } from '~/services/request/index.ts';
import { isValidUrl } from '~/utils/validations.ts';
import { requestJsonData } from '~/utils/request-json-data';
import { useIsReadyBasedOnLoginStatus } from './use-ready-state';

const MAX_EMPTY_NEXT_FEED_TRIES: number = 4;
const NEXT_FEED_TIMEOUT: number = 20_000;
const NEXT_FEED_SKELETON_ENTRIES: number = 5;

function nextFeedUrlSetter({
  feed,
  setNextFeedUrl,
}: {
  feed: Feed | undefined;
  setNextFeedUrl: any;
}): void {
  const nextFeedUrl: string | undefined = feed?.next;

  const isValidNextFeedUrl: boolean = isValidUrl(nextFeedUrl);

  if (!isValidNextFeedUrl) {
    setNextFeedUrl(undefined);
    return;
  }

  setNextFeedUrl(nextFeedUrl);
}

export function useNextFeedPagination({
  feed,
  nextFeedUrl,
  setNextFeedUrl,
  setIsNextFeedLoading,
  isNextFeedLoading,
  isLoading,
  setFeedEntries,
  feedEntries,
  itemLimit,
  condition,
  isLoadingSkeletonsV2 = true,
  fireOnLoading,
  isLoggedIn,
}: {
  feed: Feed | undefined;
  nextFeedUrl: string | undefined;
  setNextFeedUrl: any;
  setIsNextFeedLoading: any;
  isNextFeedLoading: boolean;
  isLoading: boolean | undefined;
  setFeedEntries: any;
  feedEntries: FeedEntry[] | undefined;
  itemLimit: number | undefined;
  condition: boolean;
  isLoadingSkeletonsV2?: boolean;
  fireOnLoading?: Function;
  isLoggedIn?: boolean | null;
}): void {
  const [failedNextFeedTries, setFailedNextFeedTries] = useState<number>(0);

  useEffect(() => {
    if (isLoading) return;
    nextFeedUrlSetter({
      feed,
      setNextFeedUrl,
    });
  }, [isLoading]);

  const isReadyForNextFeed: boolean = useIsReadyBasedOnLoginStatus(isLoggedIn);

  useEffect(() => {
    if (failedNextFeedTries !== 0) {
      if (failedNextFeedTries > MAX_EMPTY_NEXT_FEED_TRIES) {
        logger.info('failedNextFeedTries exceeded');
        return;
      }

      nextFeedEffect();

      return;
    }

    if (itemLimit && Number(feed?.entry?.length) >= Number(getLimit(itemLimit)))
      return;

    const initiateNextFeedFetch: boolean =
      !!nextFeedUrl && condition && !isNextFeedLoading && isReadyForNextFeed;

    if (!initiateNextFeedFetch) return;

    nextFeedEffect();
  }, [
    condition,
    feedEntries?.length,
    failedNextFeedTries,
    isNextFeedLoading,
    nextFeedUrl,
    isReadyForNextFeed,
  ]);

  useEffect(() => {
    if (!isLoadingSkeletonsV2 || !isNextFeedLoading) return;

    setFeedEntries((prevFeedEntries: Feed[]) => {
      if (!Array.isArray(prevFeedEntries)) return prevFeedEntries;
      const newFeedEntries = [
        ...prevFeedEntries,
        ...generateSkeletonEntries({ length: NEXT_FEED_SKELETON_ENTRIES }),
      ];
      return newFeedEntries;
    });
  }, [isNextFeedLoading]);

  async function nextFeedEffect(): Promise<void> {
    try {
      if (!nextFeedUrl) throw Error('Next feed url is undefined');

      setIsNextFeedLoading(true);

      if (fireOnLoading) fireOnLoading();

      const clientFeedUrl: string = getClientFeedUrl(nextFeedUrl);

      const isAddSlash = clientFeedUrl?.startsWith('/') ? '' : '/';

      const nextFeedResponse = await requestJsonData({
        url: `${isAddSlash}${clientFeedUrl}`,
        timeout: NEXT_FEED_TIMEOUT,
      });

      if (!nextFeedResponse || nextFeedResponse?.error) {
        setFeedEntries((prevFeedEntries: Feed[]) => {
          return filterSkeletonEntries([...prevFeedEntries]);
        });
        throw Error('Error fetching next feed');
      }

      const nextFeedEntries: FeedEntry[] = nextFeedResponse?.entry;

      const isEmptyEntries: boolean = isEmptyArray(nextFeedEntries);

      const nextFeedAdditionalNext: string | undefined = nextFeedResponse?.next;

      const isSameUrl: boolean = nextFeedUrl === nextFeedAdditionalNext;

      const isNextFeedAdditionalNext: boolean =
        !isSameUrl && isValidUrl(nextFeedAdditionalNext);

      if (!isEmptyEntries && nextFeedEntries?.length > 0) {
        setFeedEntries((prevFeedEntries: Feed[]) => {
          if (!Array.isArray(nextFeedEntries)) return prevFeedEntries;
          const newFeedEntries = filterSkeletonEntries([
            ...prevFeedEntries,
            ...nextFeedEntries,
          ]);

          const limit = getLimit(itemLimit);
          if (limit) return newFeedEntries.slice(0, limit);
          return newFeedEntries;
        });
      }

      if (isEmptyEntries) {
        setFeedEntries((prevFeedEntries: Feed[]) => {
          return filterSkeletonEntries(prevFeedEntries);
        });
      }

      if (isNextFeedAdditionalNext) {
        nextFeedUrlSetter({
          feed: nextFeedResponse,
          setNextFeedUrl,
        });
      } else {
        setNextFeedUrl(undefined);
        return;
      }

      if (isEmptyEntries && isNextFeedAdditionalNext) {
        setFailedNextFeedTries((prev: number) => prev + 1);
        return;
      }
    } catch (error: any) {
      setNextFeedUrl(undefined);

      if (error?.name === 'AbortError') {
        logger.info('AbortError: Next feed fetch timed out');
      } else {
        logger.error(`Error: Next feed fetch: ${error.message}`);
      }
    } finally {
      setIsNextFeedLoading(false);
    }
  }
}
